import React, { useState, useEffect } from "react";
import axios from "axios";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import DataTable from "../components/ui/DataTable";
import FormField from "../components/ui/FormField";
import TabBar from "../components/ui/TabBar";
import useAuth from "../hooks/useAuth";
import { FiRefreshCw } from "react-icons/fi";

const TABS = [
  { id: "list", label: "Seasons" },
  { id: "add", label: "Add Season" },
];

export default function Seasons() {
  const [tab, setTab] = useState("list");
  const { refreshToken } = useAuth();
  const [seasons, setSeasons] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    season_name: "",
    start_date: "",
    end_date: "",
    target_kg: "",
    notes: "",
    is_active: 1,
  });

  /* ================= FETCH SEASONS ================= */

  const fetchSeasons = async () => {
    try {
      const token = sessionStorage.getItem("maaze_token");

      const res = await axios.get(
        "https://maize.norissolutions.in/Php/public/api/seasons",
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSeasons(res.data?.data || []);
    } catch (err) {
      console.log(err.response?.data);
    }
  };

  useEffect(() => {
    fetchSeasons();
  }, []);

  /* ================= SAVE (POST / PUT) ================= */

  const handleSubmit = async () => {
    try {
      const token = sessionStorage.getItem("maaze_token");

      const payload = {
        ...form,
        target_kg: Number(form.target_kg),
      };

      if (editingId) {
        await axios.put(
          `https://maize.norissolutions.in/Php/public/api/seasons/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          "https://maize.norissolutions.in/Php/public/api/seasons",
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setEditingId(null);

      setForm({
        season_name: "",
        start_date: "",
        end_date: "",
        target_kg: "",
        notes: "",
        is_active: 1,
      });

      fetchSeasons();
      setTab("list");
    } catch (err) {
      console.log(err.response?.data);
    }
  };

  /* ================= EDIT ================= */

  const handleEdit = (row) => {
    setForm({
      season_name: row.season_name,
      start_date: row.start_date,
      end_date: row.end_date,
      target_kg: row.target_kg,
      notes: row.notes,
      is_active: row.is_active,
    });

    setEditingId(row.season_id);
    setTab("add");
  };

  /* ================= ACTIVATE ================= */

  const handleActivate = async (id) => {
    try {
      const token = sessionStorage.getItem("maaze_token");

      await axios.patch(
        `https://maize.norissolutions.in/Php/public/api/seasons/${id}/activate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchSeasons();
    } catch (err) {
      console.log(err.response?.data);
    }
  };

  /* ================= TABLE COLUMNS ================= */

  const COLUMNS = [
    {
      key: "season_id",
      label: "ID",
      render: (v) => (
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
          {v}
        </span>
      ),
    },
    {
      key: "season_name",
      label: "Season",
      render: (v) => <strong>🌱 {v}</strong>,
    },
    { key: "start_date", label: "Start Date" },
    { key: "end_date", label: "End Date" },
    { key: "target_kg", label: "Target (KG)" },
    {
      key: "is_active",
      label: "Status",
      render: (v) => (v === 1 ? "Active" : "Inactive"),
    },
    {
      key: "_actions",
      label: "Actions",
      render: (v, row) => (
        <>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}>
            ✏️
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleActivate(row.season_id)}
          >
            ⚡
          </Button>
        </>
      ),
    },
  ];

  /* ================= UI ================= */

  return (
    <div>
      <PageHeader title="Seasons" subtitle="Manage production seasons">
        <FiRefreshCw className="page-refresh" onClick={refreshToken} />
      </PageHeader>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {tab === "list" && (
        <Card
          title="All Seasons"
          action={
            <Button size="sm" onClick={() => setTab("add")}>
              + Add Season
            </Button>
          }
          noPad
        >
          <DataTable columns={COLUMNS} rows={seasons} />
        </Card>
      )}

      {tab === "add" && (
        <Card title="Add New Season">
          <div className="form-grid">
            <FormField
              label="Season Name"
              type="text"
              value={form.season_name}
              onChange={(e) =>
                setForm({ ...form, season_name: e.target.value })
              }
            />

            <FormField
              label="Start Date"
              type="date"
              value={form.start_date}
              onChange={(e) =>
                setForm({ ...form, start_date: e.target.value })
              }
            />

            <FormField
              label="End Date"
              type="date"
              value={form.end_date}
              onChange={(e) =>
                setForm({ ...form, end_date: e.target.value })
              }
            />

            <FormField
              label="Target (KG)"
              type="number"
              value={form.target_kg}
              onChange={(e) =>
                setForm({ ...form, target_kg: e.target.value })
              }
            />

            <FormField
              label="Notes"
              type="textarea"
              value={form.notes}
              onChange={(e) =>
                setForm({ ...form, notes: e.target.value })
              }
            />
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <Button onClick={handleSubmit}>
              {editingId ? "Update Season" : "Save Season"}
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setEditingId(null);
                setForm({
                  season_name: "",
                  start_date: "",
                  end_date: "",
                  target_kg: "",
                  notes: "",
                  is_active: 1,
                });
                setTab("list");
              }}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}